import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
} from "@mui/material";
import { ContentCut, FilterAlt, SellOutlined, Tune } from "@mui/icons-material";
import {
  AnnotationDialog,
  ASIFilterDialog,
  ExtractDialog,
  NLMSFilterDialog,
  SingleChannelActiveDialog,
  SngSettingDialog,
  SngSettingDialogNEW,
  SyncDialog,
  SyncSelectedDialog,
  UntaggedRegionsDialog,
} from "../dialogs";
import { TimeDisplay } from "./TimeDisplay";
import { useAlerts } from "../../store/alerts/hooks";
import { useAnnotations } from "../../store/annotations/hooks";
import { useChannels } from "../../store/channels/hooks";
import { useEditor } from "../../store/editor/hooks";
import { useFeature } from "flagged";
import { SYNC } from "../../FeatureFlags";
import { useModals } from "../../store/dialogs/hooks";
import { loadCase } from "../../pages/app/utils/dataLoader";
import { pluralize } from "../../pages/app/utils/stringHelpers";
import { GlimpseAlert, SngAlert, SyncAlert } from "../alerts";
import { ASI, NLMS, NOT_TAGGED } from "../../pages/app/utils/constants";

export const EditorMenu = ({
  caseId,
  clipLength,
  isNoiseFiltering,
  mode,
  numberOfChannels,
  onCutRegion,
  onGlimpseFilter,
  onMergeRegion,
  onNormalizeFilter,
  onSngFilter,
  onSyncFilter,
  onAsiFilter,
  onNlmsFilter,
  onTagRegion,
  readOnly,
  sampleRate,
  surferLoading,
  syncSelected,
  working,
}) => {
  const useSync = useFeature(SYNC);
  const { openSyncAlert, openSngAlert, openGlimpseAlert } = useAlerts();
  const { annotations, isSaving, untaggedAnnotations } = useAnnotations();
  const { isNotMonoActiveChannel } = useChannels();
  const {
    clipTime,
    originalTime,
    selectedTag,
    canPerformOperations,
    isUntaggedSelected,
  } = useEditor();
  const {
    openAnnotationDialog,
    openASIFilterDialog,
    openNLMSFilterDialog,
    openExtractDialog,
    openSingleChannelActiveDialog,
    openSngFilterDialog,
    openSngFilterDialogNEW,
    openSyncDialog,
    openFFTSettingsDialog,
    openUntaggedRegionsDialog,
    closeSyncSelectedDialog,
    openSyncSelectedDialog,
  } = useModals();
  const regions = annotations;
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentCase, setCurrentCase] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [filterName, setFilterName] = useState("");
  const hasGlimpseOptions = loaded && currentCase?.numberOfClips > 1;
  const open = Boolean(anchorEl);
  const isLimited = mode === "sng" || mode === "sync"; // limited = no annotation, no cut, no merge
  const handleSyncSelectedClose = () => closeSyncSelectedDialog();
  const handleSyncSelectedContinue = () => {
    closeSyncSelectedDialog();
    showSyncDialog();
  };

  const load = useCallback(async () => {
    const currCase = await loadCase(caseId);
    setCurrentCase(currCase);
    setLoaded(true);
  }, [caseId]);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleGlimpseClick = () =>
    onGlimpseFilter && hasGlimpseOptions
      ? onGlimpseFilter()
      : (handleMenuClose(), openGlimpseAlert());

  const handleSyncClick = () => {
    if (
      regions?.map((item) => item.tag).filter((item) => item === selectedTag)
        .length > 1
    ) {
      handleMenuClose();
      openSyncAlert();
    } else if (syncSelected) {
      handleMenuClose();
      openSyncSelectedDialog();
    } else {
      showSyncDialog();
    }
  };

  const syncDisabled =
    !selectedTag ||
    selectedTag === NOT_TAGGED ||
    regions.length < 1 ||
    numberOfChannels < 2 ||
    !isNotMonoActiveChannel;

  const sngDisabled =
    !selectedTag ||
    selectedTag === NOT_TAGGED ||
    regions.length < 1 ||
    (numberOfChannels !== 1 && isNotMonoActiveChannel);

  const monoFilterDisabled = numberOfChannels > 1 && isNotMonoActiveChannel;
  const createModelDisabled = !canPerformOperations || regions.length < 1;
  const glimpseDisabled =
    numberOfChannels < 2 || isLimited || !isNotMonoActiveChannel;

  const untaggedAnnotationMessage = `You have ${untaggedAnnotations.length}
   untagged region${pluralize(untaggedAnnotations.length, "s")}
   in the current clip that will be removed if you continue. Continue
   to proceed with this action or cancel to return to the editor.`;

  const tagRegion = (name) => {
    if (onTagRegion) {
      onTagRegion(name);
    }
  };

  const mergeRegion = (tag, comment) => {
    if (onMergeRegion) {
      onMergeRegion(tag, comment);
    }
  };
  const cutRegion = (selectedTag) => {
    if (onCutRegion) {
      onCutRegion(selectedTag);
    }
  };

  const handleTrimClick = () =>
    Boolean(untaggedAnnotations?.length)
      ? openUntaggedRegionsDialog()
      : cutRegion(selectedTag);

  const runSngFilter = () => {
    if (onSngFilter) {
      onSngFilter();
    }
  };

  const runAsiFilter = (formValues) => {
    if (onAsiFilter) {
      onAsiFilter(formValues);
    }
  };

  const handleAsiClick = () => {
    handleMenuClose();

    if (numberOfChannels === 1) {
      openASIFilterDialog();
    } else if (numberOfChannels > 1 && !isNotMonoActiveChannel) {
      setFilterName(ASI);
      openSingleChannelActiveDialog();
    }
  };

  const runNlmsFilter = (formValues) => {
    if (onNlmsFilter) {
      onNlmsFilter(formValues);
    }
  };

  const handleNlmsClick = () => {
    handleMenuClose();

    if (numberOfChannels === 1) {
      openNLMSFilterDialog();
    } else if (numberOfChannels > 1 && !isNotMonoActiveChannel) {
      setFilterName(NLMS);
      openSingleChannelActiveDialog();
    }
  };

  const normalizeFilter = () => {
    handleMenuClose();
    if (onNormalizeFilter) {
      onNormalizeFilter();
    }
  };

  const handleCreateModel = () => {
    handleMenuClose();
    openExtractDialog();
  };

  const sngShowDialog = () => {
    handleMenuClose();
    openSngFilterDialog();
  };

  const handleSngShowDialogNEWClick = () => {
    if (
      regions?.map((item) => item.tag).filter((item) => item === selectedTag)
        .length > 1
    ) {
      handleMenuClose();
      openSngAlert();
    } else {
      handleMenuClose();
      openSngFilterDialogNEW();
    }
  };

  const runSync = (formValues) => {
    if (onSyncFilter) {
      onSyncFilter(formValues);
    }
  };

  const showSyncDialog = () => {
    handleMenuClose();
    openSyncDialog();
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        closeAfterTransition={false}
        slotProps={{ list: { "aria-labelledby": "basic-button" } }}
      >
        {!readOnly && (
          <Tooltip
            placement="left"
            arrow
            title={
              createModelDisabled &&
              "Annotate and select reference annotation to enable"
            }
          >
            <span>
              <MenuItem
                disabled={createModelDisabled || isNoiseFiltering}
                onClick={handleCreateModel}
              >
                Create Model
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Tooltip
          placement="left"
          arrow
          title={glimpseDisabled && "Must be multichannel"}
        >
          <span>
            <MenuItem
              disabled={glimpseDisabled || isNoiseFiltering}
              onClick={handleGlimpseClick}
            >
              Focus (GLIMPSE)
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem disabled={isNoiseFiltering} onClick={normalizeFilter}>
          Normalize
        </MenuItem>
        {useSync && (
          <Tooltip
            placement="left"
            arrow
            title={
              syncDisabled &&
              "Provide at least two active channels, annotate, and have alignment reference annotation selected to enable"
            }
          >
            <span>
              <MenuItem
                disabled={syncDisabled || isNoiseFiltering}
                onClick={handleSyncClick}
              >
                Align & Combine
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Divider sx={{ my: 1 }} />
        {mode === "sng" ? (
          <MenuItem onClick={sngShowDialog}>
            Broadband Noise Filter (SNG)
          </MenuItem>
        ) : (
          <Tooltip
            placement="left"
            arrow
            title={
              sngDisabled &&
              "Delete all but one channel, annotate, and select a reference annotation to enable"
            }
          >
            <span>
              <MenuItem
                disabled={sngDisabled}
                onClick={handleSngShowDialogNEWClick}
              >
                Noise Gate (Spectral)
              </MenuItem>
            </span>
          </Tooltip>
        )}

        <Tooltip
          placement="left"
          arrow
          title={monoFilterDisabled && "Delete all but one channel to enable"}
        >
          <span>
            <MenuItem
              disabled={monoFilterDisabled || isNoiseFiltering}
              onClick={handleAsiClick}
            >
              ASI Filter
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          placement="left"
          arrow
          title={monoFilterDisabled && "Delete all but one channel to enable"}
        >
          <span>
            <MenuItem
              disabled={monoFilterDisabled || isNoiseFiltering}
              onClick={handleNlmsClick}
            >
              NLMS Filter
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          flexDirection: "column",
          mx: 2,
          my: 1,
          px: 4,
          py: 0,
          position: "relative",
          zIndex: 150,
        }}
      >
        <Box
          display="flex"
          height={40}
          justifyContent="space-between"
          alignItems="center"
          width={1}
        >
          {!isLimited ? (
            <Box display="flex" gap={0.5} width={0.1}>
              {!readOnly && (
                <>
                  <Tooltip title="Create Annotation">
                    <span>
                      <IconButton
                        disabled={
                          isSaving || surferLoading || !isUntaggedSelected
                        }
                        onClick={() => openAnnotationDialog()}
                      >
                        <SellOutlined
                          color={
                            isSaving || surferLoading || !isUntaggedSelected
                              ? "disabled"
                              : "primary"
                          }
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Trim Content">
                    <span>
                      <IconButton
                        disabled={
                          isSaving || !canPerformOperations || surferLoading
                        }
                        onClick={handleTrimClick}
                      >
                        <ContentCut
                          color={
                            isSaving || !canPerformOperations || surferLoading
                              ? "disabled"
                              : "primary"
                          }
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          ) : (
            <Box display="flex" gap={0.5} width={0.1} />
          )}
          <Box display="flex" gap={2} width={0.8}>
            <Box display="flex" flex={1} justifyContent="flex-end">
              <TimeDisplay label="Clip Time" value={clipTime.toFixed(3)} />
            </Box>
            <Box display="flex" flex={1} justifyContent="flex-start">
              <TimeDisplay
                label="Original Time"
                value={originalTime.toFixed(3)}
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={0.5} width={0.1}>
            {!readOnly && (
              <>
                <Tooltip title="FFT Settings">
                  <span>
                    <IconButton
                      aria-label="FFT"
                      onClick={() => openFFTSettingsDialog()}
                    >
                      <Tune
                        color={
                          isSaving || surferLoading ? "disabled" : "primary"
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Filters">
                  <span>
                    <IconButton
                      aria-label="Filters"
                      disabled={isSaving || surferLoading}
                      onClick={handleMenuClick}
                    >
                      <FilterAlt
                        color={
                          isSaving || surferLoading ? "disabled" : "primary"
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
        <Box
          bottom={-1}
          left="50%"
          position="absolute"
          width={0.975}
          sx={{
            transform: "translateX(-50%)",
          }}
        >
          {working && <LinearProgress />}
        </Box>
      </Paper>
      <AnnotationDialog onCreate={tagRegion} />
      <ASIFilterDialog onRun={runAsiFilter} sampleRate={sampleRate} />
      <ExtractDialog
        lastValue={
          selectedTag || regions.filter((item) => Boolean(item.tag))[0]?.tag
        }
        onCreate={mergeRegion}
      />
      <NLMSFilterDialog onRun={runNlmsFilter} sampleRate={sampleRate} />
      <SingleChannelActiveDialog filterName={filterName} />
      <SngSettingDialog onRun={runSngFilter} />
      <SngSettingDialogNEW onRun={runSngFilter} />
      <SyncDialog
        clipLength={clipLength}
        sampleRate={sampleRate}
        numberOfChannels={numberOfChannels}
        onRun={runSync}
      />
      <SyncSelectedDialog
        onSyncSelectedClose={handleSyncSelectedClose}
        onSyncSelectedContinue={handleSyncSelectedContinue}
      />
      <UntaggedRegionsDialog
        cutRegion={cutRegion}
        selectedTag={selectedTag}
        untaggedAnnotationMessage={untaggedAnnotationMessage}
      />
      <GlimpseAlert />
      <SngAlert regions={regions} selectedTag={selectedTag} />
      <SyncAlert regions={regions} selectedTag={selectedTag} />
    </>
  );
};
